import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ADMIN } from '@/constants/testIds';

const EMPTY = {
  name: '', code: '', duration_minutes: 30, buffer_minutes: 5,
  color: '#292524', icon: 'Calendar', eligible_roles: ['audiologist'],
  preparation_instructions: '', reminders_enabled: true, active: true,
};

export default function AppointmentTypesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/appointment-types'); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };
  const save = async () => {
    try {
      const payload = {
        ...form,
        duration_minutes: parseInt(form.duration_minutes) || 30,
        buffer_minutes: parseInt(form.buffer_minutes) || 0,
      };
      if (editing) await api.patch(`/appointment-types/${editing.id}`, payload);
      else await api.post('/appointment-types', payload);
      toast.success('Saved'); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    await api.delete(`/appointment-types/${r.id}`); load();
  };

  return (
    <div>
      <AdminHeader
        title="Appointment Types"
        description="Duration, buffers, colours, and configuration for each visit type."
        onCreate={openCreate}
        createLabel="New Type"
        createTestId={ADMIN.createButton('appointment-type')}
      />
      <AdminTable
        loading={loading} rows={rows}
        columns={[
          { key: 'name', label: 'Name', render: (r) => (
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: r.color }} />
              <span className="font-medium">{r.name}</span>
            </div>
          )},
          { key: 'code', label: 'Code' },
          { key: 'duration', label: 'Duration', render: (r) => `${r.duration_minutes} min` },
          { key: 'buffer', label: 'Buffer', render: (r) => `${r.buffer_minutes} min` },
          { key: 'active', label: 'Active', render: (r) => r.active ? '✓' : '—' },
        ]}
        rowTestId={ADMIN.row}
        onEdit={openEdit} onDelete={remove}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit Type' : 'New Appointment Type'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9" data-testid="apt-type-field-name" />
              </div>
              <div>
                <Label>Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="h-9" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label>Buffer (min)</Label>
                <Input type="number" value={form.buffer_minutes} onChange={(e) => setForm({ ...form, buffer_minutes: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label>Colour</Label>
                <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 p-1" />
              </div>
            </div>
            <div>
              <Label>Preparation Instructions</Label>
              <Textarea rows={2} value={form.preparation_instructions || ''} onChange={(e) => setForm({ ...form, preparation_instructions: e.target.value })} className="resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid={ADMIN.saveButton}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
